"use client";

import { c as _c } from "react/compiler-runtime";
import { BuilderInterlinkPreviewCard, BuilderInterlinkPreviewSkeleton } from "@multi-tenant-cms/builder/ui/interlink/BuilderInterlinkPreviewCard";
import type { BuilderInterlinkLinkPreview } from "@multi-tenant-cms/builder/ui/interlink/types";
import { isInterlinkHoverPreviewDisabled } from "@multi-tenant-cms/interlink";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
function findPreviewableInterlinkAnchor(from: EventTarget | null, root: HTMLElement): HTMLElement | null {
  const raw = (from as HTMLElement | null)?.closest("a.interlink[data-interlink-entry-id], a.interlink[data-interlink-glossary-term-id], span.interlink[data-interlink-glossary-term-id]");
  const anchor = raw instanceof HTMLElement ? raw : null;
  if (!(anchor && root.contains(anchor))) {
    return null;
  }
  if (isInterlinkHoverPreviewDisabled(anchor)) {
    return null;
  }
  return anchor;
}
type PopState = {
  status: "loading";
  x: number;
  y: number;
} | ({
  status: "ready";
  x: number;
  y: number;
} & BuilderInterlinkLinkPreview);

/** Above public chrome; below custom-code overlays. */
const PREVIEW_Z_INDEX = 2_147_483_640;
const SHOW_DELAY_MS = 220;
const HIDE_DELAY_MS = 160;
const CURSOR_OFFSET = 12;
const VIEWPORT_MARGIN = 12;
function previewFromPayload(data: BuilderInterlinkLinkPreview, clientX: number, clientY: number): PopState {
  return {
    status: "ready",
    x: clientX,
    y: clientY,
    ...data
  };
}
function readAnchorPreviewFallback(anchor: HTMLElement): BuilderInterlinkLinkPreview {
  return {
    title: anchor.getAttribute("data-interlink-title")?.trim() ?? "",
    excerpt: anchor.getAttribute("data-interlink-excerpt")?.trim() ?? "",
    urlPath: anchor.getAttribute("data-interlink-path")?.trim() ?? "",
    featuredImageUrl: null
  };
}
function hasPreviewContent(data: BuilderInterlinkLinkPreview): boolean {
  return Boolean(data.title?.trim() || data.excerpt?.trim() || data.urlPath?.trim() || data.featuredImageUrl);
}
type TooltipPosition = {
  left: number;
  top: number;
  visible: boolean;
};
type InterlinkPreviewTooltipProps = {
  pop: PopState;
  position: TooltipPosition;
  tooltipRef: React.RefObject<HTMLDivElement | null>;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
};
function InterlinkPreviewTooltip(t0) {
  const $ = _c(17);
  const {
    pop,
    position,
    tooltipRef,
    onPointerEnter,
    onPointerLeave
  } = t0;
  const t1 = position.visible ? 1 : 0;
  const t2 = position.visible ? "translateY(0)" : "translateY(2px)";
  let t3;
  if ($[0] !== position.left || $[1] !== position.top || $[2] !== t1 || $[3] !== t2) {
    t3 = {
      left: position.left,
      top: position.top,
      opacity: t1,
      transform: t2,
      zIndex: PREVIEW_Z_INDEX,
      backgroundColor: "var(--tt-color-bg, #161b22)",
      color: "var(--tt-color-fg, #e6edf3)",
      border: "1px solid color-mix(in srgb, var(--tt-color-fg, #e6edf3) 10%, transparent)"
    };
    $[0] = position.left;
    $[1] = position.top;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== pop.excerpt || $[6] !== pop.featuredImageUrl || $[7] !== pop.status || $[8] !== pop.title || $[9] !== pop.urlPath) {
    t4 = pop.status === "loading" ? <BuilderInterlinkPreviewSkeleton /> : <a className="block no-underline" href={pop.urlPath || undefined} style={{
      color: "inherit"
    }} tabIndex={-1}><BuilderInterlinkPreviewCard excerpt={pop.excerpt} featuredImageUrl={pop.featuredImageUrl} title={pop.title} urlPath={pop.urlPath} /></a>;
    $[5] = pop.excerpt;
    $[6] = pop.featuredImageUrl;
    $[7] = pop.status;
    $[8] = pop.title;
    $[9] = pop.urlPath;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== onPointerEnter || $[12] !== onPointerLeave || $[13] !== t3 || $[14] !== t4 || $[15] !== tooltipRef) {
    t5 = <div aria-live="polite" className="pointer-events-auto fixed isolate overflow-hidden rounded-xl shadow-xl transition-[opacity,transform] duration-150 ease-out" data-interlink-preview="" onPointerEnter={onPointerEnter} onPointerLeave={onPointerLeave} ref={tooltipRef} role="tooltip" style={t3}>{t4}</div>;
    $[11] = onPointerEnter;
    $[12] = onPointerLeave;
    $[13] = t3;
    $[14] = t4;
    $[15] = tooltipRef;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}

/**
 * Hover / focus preview for `a.interlink[data-interlink-entry-id]` inside public builder HTML.
 */
export function InterlinkPreviewRoot({
  apiBasePath = "",
  children
}: {
  /** Deployment region path prefix (e.g. `/us-uk`), required so the preview
   * fetch is routed to the same regional backend as the current page. */
  apiBasePath?: string;
  children: React.ReactNode;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const [pop, setPop] = useState<PopState | null>(null);
  const [position, setPosition] = useState<TooltipPosition>({
    left: -9999,
    top: -9999,
    visible: false
  });
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cacheRef = useRef(new Map<string, BuilderInterlinkLinkPreview>());
  const abortRef = useRef<AbortController | null>(null);
  const activeAnchorRef = useRef<HTMLElement | null>(null);
  const isOverTooltipRef = useRef(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Position after the tooltip renders so its measured size can be clamped
  // inside the viewport (near screen edges it flips above/left of the cursor
  // instead of rendering off-screen).
  useLayoutEffect(() => {
    if (!pop) {
      setPosition(prev => prev.visible ? {
        ...prev,
        visible: false
      } : prev);
      return;
    }
    const el = tooltipRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    let left = pop.x + CURSOR_OFFSET;
    let top = pop.y + CURSOR_OFFSET;
    if (left + rect.width > window.innerWidth - VIEWPORT_MARGIN) {
      left = Math.max(VIEWPORT_MARGIN, window.innerWidth - rect.width - VIEWPORT_MARGIN);
    }
    if (top + rect.height > window.innerHeight - VIEWPORT_MARGIN) {
      const above = pop.y - rect.height - CURSOR_OFFSET;
      top = above > VIEWPORT_MARGIN ? above : Math.max(VIEWPORT_MARGIN, window.innerHeight - rect.height - VIEWPORT_MARGIN);
    }
    setPosition({
      left,
      top,
      visible: true
    });
  }, [pop]);
  useEffect(() => {
    const clearShowTimer = () => {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
      }
      showTimerRef.current = null;
    };
    const clearHideTimer = () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      hideTimerRef.current = null;
    };
    const scheduleHide = () => {
      clearHideTimer();
      hideTimerRef.current = setTimeout(() => {
        if (!(isOverTooltipRef.current || activeAnchorRef.current)) {
          setPop(null);
        }
      }, HIDE_DELAY_MS);
    };
    const root = rootRef.current;
    if (!root) {
      return;
    }
    const load = async (anchor: HTMLElement, clientX: number, clientY: number) => {
      const entryId = anchor.getAttribute("data-interlink-entry-id");
      const glossaryTermId = anchor.getAttribute("data-interlink-glossary-term-id");
      const fallback = readAnchorPreviewFallback(anchor);
      const cacheKey = entryId ? `entry:${entryId}` : glossaryTermId ? `glossary:${glossaryTermId}` : "";
      if (!cacheKey) {
        return;
      }
      const cached = cacheRef.current.get(cacheKey);
      if (cached) {
        setPop(previewFromPayload(cached, clientX, clientY));
        return;
      }
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setPop({
        status: "loading",
        x: clientX,
        y: clientY
      });
      const showFallbackOrClear = () => {
        if (abortRef.current !== ac) {
          return;
        }
        if (hasPreviewContent(fallback)) {
          setPop(previewFromPayload(fallback, clientX, clientY));
          return;
        }
        setPop(null);
      };
      try {
        const query = glossaryTermId ? `glossaryTermId=${encodeURIComponent(glossaryTermId)}` : `entryId=${encodeURIComponent(entryId ?? "")}`;
        const res = await fetch(`${apiBasePath}/api/v1/link-preview?${query}`, {
          signal: ac.signal
        });
        if (abortRef.current !== ac) {
          return;
        }
        if (!res.ok) {
          showFallbackOrClear();
          return;
        }
        const data = (await res.json()) as BuilderInterlinkLinkPreview;
        if (abortRef.current !== ac) {
          return;
        }
        cacheRef.current.set(cacheKey, data);
        setPop(previewFromPayload(data, clientX, clientY));
      } catch {
        if (abortRef.current !== ac) {
          return;
        }
        showFallbackOrClear();
      }
    };
    const onPointerOver = (e: PointerEvent) => {
      const anchor_0 = findPreviewableInterlinkAnchor(e.target, root);
      if (!anchor_0) {
        activeAnchorRef.current = null;
        clearShowTimer();
        scheduleHide();
        return;
      }
      clearHideTimer();
      if (activeAnchorRef.current === anchor_0) {
        return;
      }
      activeAnchorRef.current = anchor_0;
      clearShowTimer();
      showTimerRef.current = setTimeout(() => {
        load(anchor_0, e.clientX, e.clientY).catch(() => {
          // ignore preview failures
        });
      }, SHOW_DELAY_MS);
    };
    const onPointerOut = (e_0: PointerEvent) => {
      const next = e_0.relatedTarget as Node | null;
      if (tooltipRef.current?.contains(next)) {
        return;
      }
      if (next && root.contains(next)) {
        const still = findPreviewableInterlinkAnchor(next, root);
        if (still) {
          return;
        }
      }
      activeAnchorRef.current = null;
      clearShowTimer();
      scheduleHide();
    };
    root.addEventListener("pointerover", onPointerOver);
    root.addEventListener("pointerout", onPointerOut);
    return () => {
      activeAnchorRef.current = null;
      isOverTooltipRef.current = false;
      clearShowTimer();
      clearHideTimer();
      abortRef.current?.abort();
      root.removeEventListener("pointerover", onPointerOver);
      root.removeEventListener("pointerout", onPointerOut);
    };
  }, [apiBasePath]);
  const onTooltipPointerEnter = () => {
    isOverTooltipRef.current = true;
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };
  const onTooltipPointerLeave = () => {
    isOverTooltipRef.current = false;
    if (!activeAnchorRef.current) {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      hideTimerRef.current = setTimeout(() => {
        if (!(isOverTooltipRef.current || activeAnchorRef.current)) {
          setPop(null);
        }
      }, HIDE_DELAY_MS);
    }
  };
  return <div className="relative" ref={rootRef}>
			{children}
			{mounted && pop ? createPortal(<InterlinkPreviewTooltip onPointerEnter={onTooltipPointerEnter} onPointerLeave={onTooltipPointerLeave} pop={pop} position={position} tooltipRef={tooltipRef} />, document.body) : null}
		</div>;
}